import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
// RolePermissionsDetail.tsx
import { Box, Button, Checkbox, Typography, useMediaQuery, useTheme } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { PageLayout } from '@ui/components/PageLayout';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import StyledPermissionTable from './components/StyledPermissionTable';
import usePermissionStyles from './usePermissionStyles';
import ControlledHCheckboxAutocomplete from '@ui/components/common/Autocomplete/ControlledHCheckboxAutocomplete';
import { ControlledFilledTextField } from '@ui/components/common/TextField';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { refreshTokenAsync } from '@application/features/auth/authThunk';
import { createRoleWithPermissionsAsync, updateRoleWithPermissionsAsync, getRoleByIdAsync, getRolePermissionsAsync, updateRoleAsync } from '@application/features/roles/rolesThunk';
import { useAppDispatch } from '@application/store/useStore';
import { toast } from 'react-toastify';
import RoleDialog from '../RoleDialog';
import AddEditRole from '../RoleDialog/AddEditRoleForm';
import { hasPermission } from '@application/helpers/permission-helper';
import { ArrowLeft2 } from 'iconsax-react';
import BDrawer from '@ui/components/common/BDrawer';
import { useRedirectOnCompanyChange } from '@ui/hooks/useRedirectOnCompanyChange';
const PERMISSIONS = ['read', 'update', 'create', 'delete'];
const getCompanyRegionGroups = (companies) => {
    const regionGroups = {};
    companies.forEach((company) => {
        const regionLabel = company.regionLabel || 'Other';
        if (!regionGroups[regionLabel]) {
            regionGroups[regionLabel] = [];
        }
        regionGroups[regionLabel].push(company.value);
    });
    return regionGroups;
};
const RolePermissionsDetail = () => {
    useRedirectOnCompanyChange('/roles-permissions');
    const theme = useTheme();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const styles = usePermissionStyles();
    const [editRole, setEditRole] = useState(false);
    const { roleId } = useParams();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
    const [openEditRoleDialog, setOpenEditRoleDialog] = useState(false);
    const loggedInUserPermissions = useSelector((state) => state.auth.currentUser?.permissions || []);
    const canCreateRole = hasPermission(loggedInUserPermissions, 'Permissions.Roles.Create');
    const canUpdateRole = hasPermission(loggedInUserPermissions, 'Permissions.Roles.Update');
    const { selectedRole, rolePermissions } = useSelector((state) => state.role);
    const companies = useSelector((state) => state.company.companies);
    const companiesByRegion = useSelector((state) => state.company.companiesByRegion);
    const [permissions, setPermissions] = useState();
    const isHappinessManagerRole = selectedRole?.categoryId === 'e6a74301-b5cd-4374-b466-610436b9bf77';
    // Role form
    const isEditMode = !!roleId && roleId !== 'new';
    const { control, handleSubmit, reset, formState: { errors } } = useForm({
        defaultValues: {
            name: '',
            companyId: '',
            companies: []
        }
    });
    const companyRegionLookup = useMemo(() => {
        const lookup = {};
        companiesByRegion.forEach((region) => {
            region.companies.forEach((company) => {
                lookup[company.id] = region.regionLabel || 'Other';
            });
        });
        return lookup;
    }, [companiesByRegion]);
    const companyOptions = useMemo(() => companies.map((c) => ({
        label: c.name,
        value: c.id.toLowerCase(),
        regionLabel: companyRegionLookup[c.id] || 'Other'
    })), [companies, companyRegionLookup]);
    const regionGroups = useMemo(() => getCompanyRegionGroups(companyOptions), [companyOptions]);
    useEffect(() => {
        if (roleId && roleId !== 'new') {
            dispatch(getRolePermissionsAsync(roleId));
        }
    }, [dispatch, roleId]);
    useEffect(() => {
        if (roleId && roleId !== 'new') {
            dispatch(getRoleByIdAsync(roleId));
        }
    }, [dispatch, roleId]);
    useEffect(() => {
        if (isEditMode) {
            setPermissions(mapApiPermissionsToTable(rolePermissions));
        }
        else {
            // For new roles, create default empty permissions structure
            const defaultPermissions = {
                VdkEvents: { read: false, update: false, create: false, delete: false },
                VdkTasks: { read: false, update: false, create: false, delete: false },
                VdkPeople: { read: false, update: false, create: false, delete: false },
                Companies: { read: false, update: false, create: false, delete: false },
                Users: { read: false, update: false, create: false, delete: false },
                Roles: { read: false, update: false, create: false, delete: false }
            };
            setPermissions(defaultPermissions);
        }
    }, [rolePermissions, isEditMode]);
    // Populate form when editing
    useEffect(() => {
        if (isEditMode && selectedRole) {
            // Convert role's companies to the format expected by the multi-select
            const companiesForSelect = selectedRole.companies?.map((company) => ({
                label: company.name,
                value: company.id
            })) || [];
            reset({
                id: selectedRole.id,
                name: selectedRole.name,
                companyId: selectedRole.companyId?.toLowerCase() || '',
                companies: companiesForSelect // Let the component handle the format
            });
        }
    }, [isEditMode, selectedRole, reset]);
    const handlePermissionChange = (module, permission, value) => {
        setPermissions((prev = {}) => {
            const modulePerms = { ...((prev && prev[module]) || {}) };
            const permIndex = PERMISSIONS.indexOf(permission);
            if (value) {
                // Turning ON permission: lock all lower levels
                for (let i = 0; i <= permIndex; i++) {
                    const perm = PERMISSIONS[i];
                    modulePerms[perm] = true;
                }
            }
            else {
                // Turning OFF permission: unlock immediate previous permission only
                if (permIndex > 0) {
                    const prevPerm = PERMISSIONS[permIndex - 1];
                    // Do NOT auto turn it OFF, just ensure it stays ON but editable
                    modulePerms[prevPerm] = true;
                }
            }
            // Always set toggled permission to current value
            modulePerms[permission] = value;
            return {
                ...prev,
                [module]: modulePerms
            };
        });
    };
    const handleEditRole = async (data) => {
        if (!data)
            return;
        const roleRequest = {
            id: data.id || '',
            name: data.name,
            companyId: data?.companyId || ''
        };
        await dispatch(updateRoleAsync(roleRequest))
            .unwrap()
            .then((response) => {
            if (response.isSuccess) {
                toast.success(response.message);
                if (isSmallScreen) {
                    setDrawerOpen(false);
                }
                else {
                    setOpenEditRoleDialog(false);
                }
                setEditRole(false);
            }
            else {
                toast.success(response.message || 'Failed to update role. Please try again.');
            }
        })
            .catch((error) => {
            const errorMessage = typeof error === 'object' && error !== null && 'message' in error
                ? error.message
                : undefined;
            toast.error(errorMessage || 'Failed to update role. Please try again.');
        });
    };
    const isPermissionDisabled = (module, permissionIndex) => {
        if (!permissions || !permissions[module])
            return false;
        const currentModule = permissions[module];
        // Find highest ON permission in this module
        let highestOnIndex = -1;
        for (let i = PERMISSIONS.length - 1; i >= 0; i--) {
            if (currentModule[PERMISSIONS[i]]) {
                highestOnIndex = i;
                break;
            }
        }
        // If a permission is lower than highest ON, disable it
        if (permissionIndex < highestOnIndex) {
            return true;
        }
        return false;
    };
    const getEditRoleData = () => {
        const model = {
            id: selectedRole?.id,
            name: selectedRole?.name,
            companyId: selectedRole?.companyId
        };
        return model;
    };
    const mapApiPermissionsToTable = (rolePermissions) => {
        const permissions = {};
        if (!rolePermissions || !rolePermissions.permissionGroups)
            return permissions;
        rolePermissions.permissionGroups.forEach((group) => {
            // Build permissions for each module dynamically
            const modulePerms = {
                read: false,
                update: false,
                create: false,
                delete: false
            };
            group.permissions.forEach((p) => {
                // Extract permission type from permissionName (e.g. "Read" from "Permissions.Companies.Read")
                const parts = p.permissionName.split('.');
                const permType = parts[parts.length - 1].toLowerCase();
                if (['read', 'update', 'create', 'delete'].includes(permType)) {
                    modulePerms[permType] = p.isAssigned;
                }
            });
            permissions[group.groupName] = modulePerms;
        });
        return permissions;
    };
    const flattenPermissions = (rolePermissions) => {
        const result = [];
        Object.entries(rolePermissions).forEach(([module, perms]) => {
            Object.entries(perms).forEach(([permType, isAssigned]) => {
                // Capitalize first letter for displayName
                const displayName = permType.charAt(0).toUpperCase() + permType.slice(1);
                // Compose permissionName as in your API: "Permissions.Module.Permission"
                const permissionName = `Permissions.${module}.${displayName}`;
                result.push({
                    displayName,
                    permissionName,
                    isAssigned
                });
            });
        });
        return result;
    };
    const flattenPermissionsToStrings = (rolePermissions) => {
        const result = [];
        Object.entries(rolePermissions).forEach(([module, perms]) => {
            Object.entries(perms).forEach(([permType, isAssigned]) => {
                if (isAssigned) {
                    // Capitalize first letter for displayName
                    const displayName = permType.charAt(0).toUpperCase() + permType.slice(1);
                    // Compose permissionName as in your API: "Permissions.Module.Permission"
                    const permissionName = `Permissions.${module}.${displayName}`;
                    result.push(permissionName);
                }
            });
        });
        return result;
    };
    const flattenPermissionsToPermissionRequests = (rolePermissions) => {
        const result = [];
        Object.entries(rolePermissions).forEach(([module, perms]) => {
            Object.entries(perms).forEach(([permType, isAssigned]) => {
                // Capitalize first letter for displayName
                const displayName = permType.charAt(0).toUpperCase() + permType.slice(1);
                // Compose permissionName as in your API: "Permissions.Module.Permission"
                const permissionName = `Permissions.${module}.${displayName}`;
                result.push({
                    permissionName,
                    isAssigned
                });
            });
        });
        return result;
    };
    const handleSaveRoleAndPermissions = async (data) => {
        if (!permissions)
            return;
        // Permission check as safety measure
        if (isEditMode && !canUpdateRole) {
            toast.error(t('common.unauthorized') || 'You do not have permission to update roles');
            return;
        }
        if (!isEditMode && !canCreateRole) {
            toast.error(t('common.unauthorized') || 'You do not have permission to create roles');
            return;
        }
        try {
            let roleResponse;
            if (isEditMode) {
                // Update existing role with permissions using merged API
                const updateRoleWithPermissionsRequest = {
                    id: data.id,
                    name: data.name,
                    categoryId: selectedRole?.categoryId,
                    companies: Array.isArray(data.companies)
                        ? data.companies.map((c) => (typeof c === 'object' && c.value ? c.value : c))
                        : [],
                    permissions: flattenPermissionsToPermissionRequests(permissions)
                };
                roleResponse = await dispatch(updateRoleWithPermissionsAsync(updateRoleWithPermissionsRequest)).unwrap();
                if (roleResponse.isSuccess) {
                    toast.success('Role and permissions updated successfully');
                    await dispatch(refreshTokenAsync());
                    navigate('/roles-permissions');
                }
                else {
                    toast.error(roleResponse.message || 'Failed to update role');
                }
            }
            else {
                // Create new role with permissions using merged API
                const createRoleWithPermissionsRequest = {
                    name: data.name,
                    categoryId: selectedRole?.categoryId,
                    companies: Array.isArray(data.companies)
                        ? data.companies.map((c) => (typeof c === 'object' && c.value ? c.value : c))
                        : [],
                    permissions: flattenPermissionsToStrings(permissions)
                };
                roleResponse = await dispatch(createRoleWithPermissionsAsync(createRoleWithPermissionsRequest)).unwrap();
                if (roleResponse.isSuccess) {
                    toast.success('Role created and permissions set successfully');
                    await dispatch(refreshTokenAsync());
                    navigate('/roles-permissions');
                }
                else {
                    toast.error(roleResponse.message || 'Failed to create role');
                }
            }
        }
        catch (error) {
            toast.error('Failed to save role and permissions');
        }
    };
    return (_jsxs(PageLayout, { title: isEditMode ? t('rolesPermissions.updateRole') : t('rolesPermissions.addRole'), showBackButton: true, onBackClick: () => navigate(-1), backIcon: _jsx(ArrowLeft2, { size: 20 }), rightContent: _jsx(Button, { variant: 'contained', size: isSmallScreen ? 'medium' : 'large', fullWidth: isSmallScreen, color: 'primary', disabled: isEditMode ? !canUpdateRole : !canCreateRole, onClick: handleSubmit(handleSaveRoleAndPermissions), children: isEditMode ? t('actions.editNow') : t('actions.addNow') }), children: [isSmallScreen ? (_jsx(BDrawer, { anchor: 'bottom', open: drawerOpen, onClose: () => {
                    setDrawerOpen(false);
                    setEditRole(false);
                }, title: t('rolesPermissions.editRole'), children: _jsx(AddEditRole, { edit: editRole, role: getEditRoleData(), onSubmit: (data) => {
                        if ('id' in data && 'companyId' in data) {
                            return handleEditRole(data);
                        }
                    }, onCancel: () => setDrawerOpen(false) }) })) : (_jsx(_Fragment, { children: openEditRoleDialog && (_jsx(RoleDialog, { open: openEditRoleDialog, edit: editRole, role: getEditRoleData(), onClose: () => {
                        setOpenEditRoleDialog(false);
                        setEditRole(false);
                    }, onSubmitRole: handleEditRole })) })), _jsxs(Grid, { container: true, spacing: 1.5, my: 1, px: 1, mr: 1.5, children: [_jsx(Grid, { size: { xs: 12, sm: 4 }, children: _jsx(ControlledFilledTextField, { bg: 'grey8', name: 'name', control: control, rules: {
                                required: t('validations.required', { field: t('labels.role') }),
                                maxLength: {
                                    value: 250,
                                    message: t('validations.maxLength', {
                                        field: t('Name'),
                                        max: 250
                                    })
                                }
                            }, errorMessage: errors.name?.message, label: t('labels.role') }) }), _jsx(Grid, { size: { xs: 12, sm: 8 }, children: _jsx(ControlledHCheckboxAutocomplete, { bg: 'grey8', control: control, label: t('labels.companies'), name: 'companies', rules: {
                                required: t('validations.required', { field: t('labels.companies') })
                            }, error: errors.companies?.message, options: companyOptions, showSelectAll: true, isSortable: false, selectAllLabel: t('actions.selectAll'), maxTags: 2, groupBy: (option) => option.regionLabel || '', renderGroup: (params) => {
                                const regionLabel = params.group;
                                const regionCompanyIds = regionGroups[regionLabel] || [];
                                // Get current field value to determine checkbox state
                                const currentCompanies = control._formValues.companies || [];
                                const currentCompanyIds = currentCompanies.map((c) => typeof c === 'object' && c.value ? c.value : c);
                                const allSelected = regionCompanyIds.length > 0 &&
                                    regionCompanyIds.every((id) => currentCompanyIds.includes(id));
                                const someSelected = !allSelected && regionCompanyIds.some((id) => currentCompanyIds.includes(id));
                                const isUngrouped = !regionLabel;
                                return (_jsxs("li", { children: [_jsxs(Box, { display: 'flex', justifyContent: 'space-between', alignItems: 'center', sx: { pr: 1 }, onClick: (e) => e.stopPropagation(), children: [_jsx(Typography, { variant: 'subtitle2', sx: { pl: 2, pt: 1, pb: 0.5, color: 'text.secondary' }, children: regionLabel }), !isUngrouped && (_jsx(Checkbox, { size: 'small', checked: allSelected, indeterminate: someSelected, onClick: (e) => {
                                                        e.stopPropagation();
                                                        if (allSelected) {
                                                            const updatedSelection = currentCompanies.filter((c) => {
                                                                const id = typeof c === 'object' && c.value ? c.value : c;
                                                                return !regionCompanyIds.includes(id);
                                                            });
                                                            control._formValues.companies = updatedSelection;
                                                            reset({ ...control._formValues });
                                                        }
                                                        else {
                                                            const newCompanies = companyOptions.filter((opt) => regionCompanyIds.includes(opt.value) &&
                                                                !currentCompanyIds.includes(opt.value));
                                                            const updatedSelection = [...currentCompanies, ...newCompanies];
                                                            control._formValues.companies = updatedSelection;
                                                            reset({ ...control._formValues });
                                                        }
                                                    }, sx: {
                                                        '&.Mui-disabled': {
                                                            opacity: 0.5
                                                        }
                                                    } }))] }), _jsx("ul", { style: { padding: 0, margin: 0 }, children: params.children })] }, params.key));
                            } }) })] }), _jsx(Box, { sx: { ...styles.permissionContainer }, children: _jsx(StyledPermissionTable, { rows: Object.keys(permissions ?? {}), rolePermissions: permissions ?? {}, onChange: handlePermissionChange, isDisabled: isPermissionDisabled, t: t, isAllDisabled: isHappinessManagerRole || !canUpdateRole }) })] }));
};
export default RolePermissionsDetail;
